import React from "react";
import { useTranslation } from "react-i18next";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { LiveTrainingClubLogo } from "./LiveTrainingClubLogo";
import "./LiveTrainingCoachEmptySetup.css";

/**
 * Coach empty-session setup: plan exercises + manage presence before starting.
 */
export function LiveTrainingCoachEmptySetup({
  user = null,
  remainingMs = 0,
  onPlanSession,
  onManagePlayers,
  onAddExercise,
  onDigitizeSession = null,
  onStartTimer = null,
  startingTimer = false,
}) {
  const { t } = useTranslation();

  const formatRemaining = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div className="lt-coach-empty">
      <div className="lt-coach-empty__pulse" aria-hidden />
      <LiveTrainingClubLogo
        user={user}
        size="lg"
        className="lt-coach-empty__club-logo"
        showName
      />
      <h2 className="lt-coach-empty__title">
        {t("liveTrainingCoachEmptyTitle")}
      </h2>
      <p className="lt-coach-empty__hint">
        {t(
          "liveTrainingCoachEmptyHint",
          "Start a timer for present players, or plan exercises first."
        )}
      </p>
      {remainingMs > 0 ? (
        <div className="lt-coach-empty__countdown">
          <span className="lt-coach-empty__countdown-label">
            {t("liveTrainingSlotRemaining")}
          </span>
          <span className="lt-coach-empty__countdown-value">
            {formatRemaining(remainingMs)}
          </span>
        </div>
      ) : null}

      <div className="lt-coach-empty__actions">
        {typeof onStartTimer === "function" ? (
          <button
            type="button"
            className="lt-coach-empty__btn lt-coach-empty__btn--primary"
            onClick={onStartTimer}
            disabled={startingTimer}
            data-tour-id="live-coach-start-timer"
          >
            <TimerOutlinedIcon fontSize="small" aria-hidden />
            {startingTimer
              ? t("liveTrainingCoachEmptyStartingTimer", "Starting timer…")
              : t("liveTrainingCoachEmptyStartTimer", "Start timer")}
          </button>
        ) : null}
        {typeof onDigitizeSession === "function" ? (
          <button
            type="button"
            className={`lt-coach-empty__btn ${
              typeof onStartTimer === "function"
                ? "lt-coach-empty__btn--secondary"
                : "lt-coach-empty__btn--primary"
            }`}
            onClick={onDigitizeSession}
          >
            <EventNoteOutlinedIcon fontSize="small" aria-hidden />
            {t("liveTrainingDigitizeSession", "Digitize this session")}
          </button>
        ) : null}
        {typeof onPlanSession === "function" ? (
          <button
            type="button"
            className={`lt-coach-empty__btn ${
              typeof onStartTimer === "function" ||
              typeof onDigitizeSession === "function"
                ? "lt-coach-empty__btn--secondary"
                : "lt-coach-empty__btn--primary"
            }`}
            onClick={onPlanSession}
          >
            <EventNoteOutlinedIcon fontSize="small" aria-hidden />
            {t("liveTrainingCoachEmptyPlan")}
          </button>
        ) : null}
        {typeof onManagePlayers === "function" ? (
          <button
            type="button"
            className="lt-coach-empty__btn lt-coach-empty__btn--secondary"
            onClick={onManagePlayers}
          >
            <GroupsOutlinedIcon fontSize="small" aria-hidden />
            {t("liveTrainingCoachEmptyPlayers")}
          </button>
        ) : null}
        {typeof onAddExercise === "function" ? (
          <button
            type="button"
            className="lt-coach-empty__btn lt-coach-empty__btn--ghost"
            onClick={onAddExercise}
          >
            <AddCircleOutlineIcon fontSize="small" aria-hidden />
            {t("liveTrainingCoachEmptyAddOne")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default LiveTrainingCoachEmptySetup;
